// src/components/profile/Profile.js
import React, {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useAuth,
} from '../../contexts/AuthContext';
import useTeamActions from '../../hooks/useTeamActions';
import useProfileActions from '../../hooks/useProfileActions';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Group as TeamIcon,
  SportsEsports as GameIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  EmojiEvents as AchievementIcon,
  LocalBar as BarIcon,
  ArrowForward as ArrowForwardIcon,
  Twitter as TwitterIcon,
  Webhook as DiscordIcon,
} from '@mui/icons-material';
import AchievementGrid from '../achievements/AchievementGrid';
import {
  achievementsService,
} from '../../services/achievements';
import {
  apiService,
} from '../../services/api';
import {
  barService,
} from '../../services/bar';
import { resolveMediaUrl } from '../../utils/mediaUrl';

function Profile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user: authUser, loading: authLoading } = useAuth();
  const { user, addGameAccount, removeGameAccount, loading: profileLoading } = useProfileActions();
  const { teams } = useTeamActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Twitch Icon Component (simple SVG)
  const TwitchIcon = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
    </svg>
  );
  
  // Social icon mapping
  const socialIconMap = {
    twitch: <TwitchIcon />,
    twitter: <TwitterIcon />,
    discord: <DiscordIcon />
  };
  
  // State for the user whose profile we're viewing
  const [displayUser, setDisplayUser] = useState(null);
  const [fetchingUser, setFetchingUser] = useState(false);
  const [fetchError, setFetchError] = useState('');
  
  // Determine if viewing own profile
  const isOwnProfile = !userId || userId === (authUser?._id || authUser?.id);
  
  // Use appropriate user data source
  const currentUser = isOwnProfile ? (user || authUser) : displayUser;
  
  // Debug logging for currentUser
  useEffect(() => {
    console.log('=== currentUser changed ===');
    console.log('isOwnProfile:', isOwnProfile);
    console.log('currentUser:', currentUser);
    console.log('currentUser._id:', currentUser?._id);
    console.log('currentUser.id:', currentUser?.id);
  }, [currentUser, isOwnProfile]);
  
  // Ensure teams is always an array, even if the API call fails
  const allTeams = teams || [];
  
  // Filter teams to show only those where the current user is a member or captain
  const userTeams = allTeams.filter(team => {
    if (!currentUser) {
      console.log('No current user for filtering');
      return false;
    }
    
    // Debug logging
    console.log('Filtering team:', team.name);
    console.log('Current user._id:', currentUser._id);
    console.log('Current user.id:', currentUser.id);
    console.log('Team captain:', team.captain);
    console.log('Team members:', team.members);
    
    // Check if user is the captain - only if captain field exists and is not undefined
    const userIdToCheck = currentUser._id || currentUser.id;
    const captainId = team.captain;
    
    // Convert both IDs to strings for comparison (handle ObjectID vs string)
    const userIdStr = String(userIdToCheck);
    const captainIdStr = String(captainId);
    
    console.log('Captain comparison:');
    console.log('  - User ID (raw):', userIdToCheck, typeof userIdToCheck);
    console.log('  - Captain ID (raw):', captainId, typeof captainId);
    console.log('  - User ID (string):', userIdStr);
    console.log('  - Captain ID (string):', captainIdStr);
    
    const isCaptain = captainId && captainIdStr === userIdStr;
    console.log('  - Is Captain:', isCaptain);
    
    if (isCaptain) {
      console.log('User is captain of:', team.name);
      return true;
    }
    
    // Check if user is in the members array
    if (team.members && Array.isArray(team.members)) {
      console.log('Checking members for team:', team.name);
      const isMember = team.members.some(member => {
        console.log('Checking member:', member);
        // Handle both string IDs and member objects
        const memberId = typeof member === 'string' ? member : (member.user_id || member._id || member.id);
        
        // Convert both to strings for comparison (handle ObjectID vs string)
        const memberIdStr = String(memberId);
        const userIdStrForMember = String(userIdToCheck);
        
        console.log('Member comparison:');
        console.log('  - Member ID (raw):', memberId, typeof memberId);
        console.log('  - Member ID (string):', memberIdStr);
        console.log('  - User ID (string):', userIdStrForMember);
        
        const isMatch = memberIdStr === userIdStrForMember;
        console.log('  - Is Match:', isMatch);
        
        if (isMatch) {
          console.log('User is member of:', team.name);
        }
        return isMatch;
      });
      if (isMember) return true;
    }
    
    console.log('User is NOT part of:', team.name);
    return false;
  });

  console.log('Filtered user teams:', userTeams.length, 'out of', allTeams.length, 'total teams');

  const [profileStats, setProfileStats] = useState({
    teamsJoined: 0,
    gamesLinked: 0,
    accountAge: 0,
    achievementPoints: 0
  });
  const [achievements, setAchievements] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [barTransactions, setBarTransactions] = useState([]);
  const [barTransactionsLoading, setBarTransactionsLoading] = useState(false);

  const [gameAccountDialog, setGameAccountDialog] = useState(false);
  const [newGameAccount, setNewGameAccount] = useState({
    gameName: '',
    accountId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirect to own profile if no userId provided
  useEffect(() => {
    if (!userId && authUser && !authLoading) {
      const ownUserId = authUser._id || authUser.id;
      navigate(`/profile/${ownUserId}`, { replace: true });
    }
  }, [userId, authUser, authLoading, navigate]);

  // Fetch user data when userId changes
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      console.log('=== Fetching user data ===');
      console.log('userId:', userId);
      console.log('isOwnProfile:', isOwnProfile);
      console.log('authUser:', authUser);
      
      // If viewing own profile, no need to fetch
      if (isOwnProfile) {
        console.log('Setting displayUser to authUser');
        setDisplayUser(authUser);
        return;
      }
      
      // Fetch the user's profile
      setFetchingUser(true);
      setFetchError('');
      try {
        console.log('Fetching user by ID:', userId);
        const result = await apiService.getUserById(userId);
        console.log('getUserById result:', result);
        if (result.success) {
          console.log('Setting displayUser to fetched data:', result.data);
          setDisplayUser(result.data);
        } else {
          setFetchError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setFetchError('Failed to load user profile');
      } finally {
        setFetchingUser(false);
      }
    };
    
    fetchUserData();
  }, [userId, isOwnProfile, authUser]);

  // Load achievements for the current user (wrapped in useCallback to avoid stale closures)
  const loadAchievements = React.useCallback(async () => {
    if (!currentUser) return;
    
    setAchievementsLoading(true);
    try {
      const userId = currentUser._id || currentUser.id;
      console.log('Loading achievements for user:', userId);
      const [achievementsResult, pointsResult] = await Promise.all([
        achievementsService.getUserAchievements(userId),
        achievementsService.getUserPoints(userId)
      ]);
      
      console.log('Achievements result:', achievementsResult);
      console.log('Points result:', pointsResult);
      
      if (achievementsResult.success) {
        setAchievements(achievementsResult.data);
      }
      
      if (pointsResult.success) {
        console.log('Setting achievement points to:', pointsResult.data.total_points || 0);
        setProfileStats(prev => ({
          ...prev,
          achievementPoints: pointsResult.data.total_points || 0
        }));
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setAchievementsLoading(false);
    }
  }, [currentUser]);

  // Load bar transactions for the current user
  const loadBarTransactions = React.useCallback(async () => {
    if (!currentUser) return;
    
    setBarTransactionsLoading(true);
    try {
      const userId = currentUser._id || currentUser.id;
      const result = await barService.getUserTransactions(userId);
      
      if (result.success) {
        setBarTransactions(result.data);
      }
    } catch (error) {
      console.error('Error loading bar transactions:', error);
    } finally {
      setBarTransactionsLoading(false);
    }
  }, [currentUser]);

  // Separate useEffect for basic profile stats (teams, games, account age)
  useEffect(() => {
    if (currentUser) {
      setProfileStats(prev => ({
        ...prev,
        teamsJoined: userTeams.length || 0,
        gamesLinked: currentUser.game_accounts?.length || 0,
        accountAge: currentUser.created_at ? 
          Math.floor((new Date() - new Date(currentUser.created_at)) / (1000 * 60 * 60 * 24)) : 0
      }));
    }
  }, [currentUser, userTeams.length]); // Use userTeams.length instead of allTeams

  // Separate useEffect for loading achievements (only when currentUser changes)
  useEffect(() => {
    if (currentUser) {
      loadAchievements();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAchievements]); // Only depends on loadAchievements (which recreates when currentUser changes)

  // Load bar transactions when currentUser changes
  useEffect(() => {
    if (currentUser) {
      loadBarTransactions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBarTransactions]);

  const handleGameAccountChange = (e) => {
    setNewGameAccount({
      ...newGameAccount,
      [e.target.name]: e.target.value
    });
  };

  const handleAddGameAccount = async () => {
    if (!newGameAccount.gameName.trim() || !newGameAccount.accountId.trim()) {
      setError('Please fill in both game name and account ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const success = await addGameAccount(newGameAccount);
      if (success) {
        setSuccess('Game account added successfully!');
        setGameAccountDialog(false);
        setNewGameAccount({ gameName: '', accountId: '' });
      } else {
        setError('Failed to add game account. Please try again.');
      }
    } catch (err) {
      setError('Failed to add game account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGameAccount = async (index) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await removeGameAccount(index);
      setSuccess('Game account removed successfully!');
    } catch (err) {
      setError('Failed to remove game account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeGameAccountDialog = () => {
    setGameAccountDialog(false);
    setNewGameAccount({ gameName: '', accountId: '' });
    setError('');
  };

  const openGameAccountDialog = () => {
    setGameAccountDialog(true);
    setError('');
    setSuccess('');
  };

  if (authLoading || profileLoading || fetchingUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (fetchError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {fetchError}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Unable to load profile information. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  const StatCard = ({ title, value, icon, color = 'primary' }) => {
    const colorMap = {
      primary: '#3D7A8C',
      secondary: '#C9A227',
      warning: '#C47B5A',
      success: '#6B8F71'
    };
    
    const cardColor = colorMap[color] || colorMap.primary;
    
    return (
      <Card 
        sx={{ 
          height: '100%',
          backgroundColor: '#241E18',
          border: `1px solid ${alpha(cardColor, 0.45)}`,
          borderRadius: 1,
          overflow: 'visible',
          '&:hover': {
            borderColor: cardColor,
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                component="div" 
                sx={{ 
                  fontWeight: 800,
                  color: cardColor,
                  mb: 0.5
                }}
              >
                {value}
              </Typography>
              <Typography 
                variant={isMobile ? "caption" : "body2"} 
                sx={{ 
                  color: alpha('#fff', 0.7),
                  fontWeight: 600
                }}
              >
                {title}
              </Typography>
            </Box>
            <Avatar 
              sx={{ 
                bgcolor: alpha(cardColor, 0.2),
                width: { xs: 48, sm: 56 }, 
                height: { xs: 48, sm: 56 },
                border: `2px solid ${alpha(cardColor, 0.3)}`,
                '& .MuiSvgIcon-root': {
                  color: cardColor,
                  fontSize: { xs: '1.5rem', sm: '1.8rem' }
                }
              }}
            >
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Viewing Other Profile Banner */}
      {!isOwnProfile && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<PersonIcon />}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Viewing {currentUser.username}'s Profile
          </Typography>
        </Alert>
      )}

      {/* Header Section - Cyberpunk Hero */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#241E18',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4,
          border: '1px solid #3D3428',
          '&:hover': {
            borderColor: '#C9A227',
          }
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundColor: '#241E18',
            zIndex: 1,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 2, p: { xs: 3, sm: 4, md: 5 } }}>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: 3, sm: 4 }
            }}
          >
            {/* Avatar with Glow */}
            <Avatar 
              sx={{ 
                width: { xs: 120, sm: 140, md: 160 }, 
                height: { xs: 120, sm: 140, md: 160 }, 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                fontWeight: 700,
                border: '3px solid #C9A227',
                color: '#14110E',
                bgcolor: '#C9A227',
              }}
              src={resolveMediaUrl(currentUser.avatar)}
            >
              {currentUser.username?.charAt(0).toUpperCase()}
            </Avatar>

            {/* Profile Info */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography 
                variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'} 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  color: '#F3EDE2', fontFamily: '"Fraunces", serif',
                  mb: 2
                }}
              >
                {currentUser.username}
              </Typography>

              {/* Badges */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip 
                  icon={<EmailIcon />} 
                  label={currentUser.email} 
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    bgcolor: alpha('#3D7A8C', 0.1),
                    color: '#3D7A8C',
                    borderColor: alpha('#3D7A8C', 0.3),
                    border: '1px solid',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: alpha('#3D7A8C', 0.2),
                    }
                  }} 
                />
                {currentUser.role === 'admin' && (
                  <Chip 
                    icon={<BadgeIcon />} 
                    label="Administrator" 
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      bgcolor: alpha('#C47B5A', 0.2),
                      color: '#C47B5A',
                      fontWeight: 700,
                      border: `1px solid ${alpha('#C47B5A', 0.4)}`,
                    }}
                  />
                )}
                <Chip 
                  icon={<CalendarIcon />} 
                  label={`${profileStats.accountAge} days active`} 
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    bgcolor: alpha('#C9A227', 0.1),
                    color: '#C9A227',
                    borderColor: alpha('#C9A227', 0.3),
                    border: '1px solid',
                    fontWeight: 600,
                  }} 
                />
              </Box>

              {/* Bio */}
              {currentUser.bio && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2.5, 
                    color: alpha('#fff', 0.8),
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    maxWidth: '600px',
                    mx: { xs: 'auto', md: 0 }
                  }}
                >
                  {currentUser.bio}
                </Typography>
              )}

              {/* Social Links */}
              {currentUser.social_links && currentUser.social_links.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, mb: 2 }}>
                  {currentUser.social_links.map((link, index) => {
                    const icon = socialIconMap[link.platform.toLowerCase()];
                    if (!icon) return null;
                    
                    return (
                      <IconButton
                        key={index}
                        onClick={() => window.open(link.url, '_blank')}
                        sx={{
                          color: '#3D7A8C',
                          bgcolor: alpha('#3D7A8C', 0.1),
                          border: `1px solid ${alpha('#3D7A8C', 0.3)}`,
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            bgcolor: alpha('#3D7A8C', 0.2),
                            borderColor: alpha('#3D7A8C', 0.5),
                            boxShadow: `0 5px 20px ${alpha('#3D7A8C', 0.4)}`,
                          }
                        }}
                      >
                        {icon}
                      </IconButton>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Settings Button */}
            {isOwnProfile && (
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/settings')}
                sx={{ 
                  alignSelf: { xs: 'center', md: 'flex-start' },
                  backgroundColor: '#E85D4C',
                  color: '#14110E',
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha('#3D7A8C', 0.5)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                  }
                }}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Teams Joined"
            value={profileStats.teamsJoined}
            icon={<TeamIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Games Linked"
            value={profileStats.gamesLinked}
            icon={<GameIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Achievement Points"
            value={profileStats.achievementPoints}
            icon={<AchievementIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Days Active"
            value={profileStats.accountAge}
            icon={<PersonIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Game Accounts Section */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: 'fit-content',
              backgroundColor: '#241E18',
              border: `1px solid ${alpha('#C9A227', 0.2)}`,
              borderRadius: 3,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#C9A227',
                  }}
                >
                  <GameIcon />
                  Game Accounts
                </Typography>
                {isOwnProfile && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openGameAccountDialog}
                    disabled={loading}
                    sx={{
                      borderColor: alpha('#C9A227', 0.5),
                      color: '#C9A227',
                      '&:hover': {
                        borderColor: '#C9A227',
                        bgcolor: alpha('#C9A227', 0.1),
                      }
                    }}
                  >
                    Add Game
                  </Button>
                )}
              </Box>
              
              {currentUser.game_accounts && currentUser.game_accounts.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {currentUser.game_accounts.map((account, index) => (
                    <ListItem 
                      key={index} 
                      divider={index < currentUser.game_accounts.length - 1}
                      sx={{
                        px: { xs: 1, sm: 2 },
                        py: 1.5,
                        '&:hover': {
                          bgcolor: alpha('#C9A227', 0.05),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <GameIcon sx={{ color: '#C9A227' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={account.game_name} 
                              size="small" 
                              sx={{
                                bgcolor: alpha('#C9A227', 0.2),
                                color: '#C9A227',
                                fontWeight: 600,
                                border: `1px solid ${alpha('#C9A227', 0.3)}`,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500, color: alpha('#fff', 0.9) }}>
                              {account.account_id}
                            </Typography>
                          </Box>
                        }
                      />
                      {isOwnProfile && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleRemoveGameAccount(index)}
                            disabled={loading}
                            sx={{
                              color: '#C47B5A',
                              '&:hover': {
                                bgcolor: alpha('#C47B5A', 0.1),
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <GameIcon sx={{ fontSize: 48, color: alpha('#C9A227', 0.3), mb: 2 }} />
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), mb: 2 }}>
                    No game accounts linked yet
                  </Typography>
                  {isOwnProfile && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openGameAccountDialog}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#E85D4C', color: '#14110E',
                        '&:hover': {
                        }
                      }}
                    >
                      Link Your First Game
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Team Affiliations Section */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: 'fit-content',
              backgroundColor: '#241E18',
              border: `1px solid ${alpha('#3D7A8C', 0.2)}`,
              borderRadius: 3,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#3D7A8C',
                  }}
                >
                  <TeamIcon />
                  Team Affiliations
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/teams')}
                  sx={{
                    borderColor: alpha('#3D7A8C', 0.5),
                    color: '#3D7A8C',
                    '&:hover': {
                      borderColor: '#3D7A8C',
                      bgcolor: alpha('#3D7A8C', 0.1),
                    }
                  }}
                >
                  Browse Teams
                </Button>
              </Box>
              
              {userTeams && userTeams.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {userTeams.map((team, index) => (
                    <ListItem 
                      key={team._id || index} 
                      divider={index < userTeams.length - 1}
                      sx={{
                        px: { xs: 1, sm: 2 },
                        py: 1.5,
                        '&:hover': {
                          bgcolor: alpha('#3D7A8C', 0.05),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <TeamIcon sx={{ color: '#3D7A8C' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: alpha('#fff', 0.9) }}>
                              {team.name}
                            </Typography>
                            {(team.captain === currentUser._id || team.captain === currentUser.id) && (
                              <Chip 
                                label="Captain" 
                                size="small" 
                                sx={{
                                  bgcolor: alpha('#3D7A8C', 0.2),
                                  color: '#3D7A8C',
                                  fontWeight: 600,
                                  border: `1px solid ${alpha('#3D7A8C', 0.3)}`,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                            {team.members?.length || 0} members
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="view"
                          onClick={() => navigate(`/teams/${team._id}`)}
                          sx={{
                            color: '#3D7A8C',
                            '&:hover': {
                              bgcolor: alpha('#3D7A8C', 0.1),
                            }
                          }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TeamIcon sx={{ fontSize: 48, color: alpha('#3D7A8C', 0.3), mb: 2 }} />
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), mb: 2 }}>
                    Not part of any teams yet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/teams')}
                    sx={{
                      backgroundColor: '#E85D4C', color: '#14110E',
                      '&:hover': {
                      }
                    }}
                  >
                    Join a Team
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bar Transactions Section */}
        {barTransactions.length > 0 && (
          <Grid item xs={12}>
            <Card 
              sx={{ 
                backgroundColor: '#241E18',
                border: `1px solid ${alpha('#6B8F71', 0.2)}`,
                borderRadius: 3,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <BarIcon 
                    sx={{ 
                      fontSize: 32, 
                      color: '#6B8F71',
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 800,
                      color: '#6B8F71',
                    }}
                  >
                    Bar Spending History
                  </Typography>
                </Box>
                {barTransactionsLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#6B8F71' }} />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Card 
                        sx={{ 
                          backgroundColor: '#241E18',
                          border: `1px solid ${alpha('#6B8F71', 0.3)}`,
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              color: '#6B8F71',
                              mb: 1
                            }}
                          >
                            Total Spent: {barTransactions.reduce((sum, t) => sum + (t.price * t.quantity), 0).toFixed(2)} CHF
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                            {barTransactions.length} transaction(s)
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    <List sx={{ p: 0 }}>
                      {barTransactions.slice(0, 3).map((transaction, index) => (
                        <React.Fragment key={transaction._id}>
                          {index > 0 && <Divider sx={{ borderColor: alpha('#6B8F71', 0.1) }} />}
                          <ListItem
                            sx={{
                              px: { xs: 1, sm: 2 },
                              py: 2,
                              '&:hover': {
                                bgcolor: alpha('#6B8F71', 0.05),
                              }
                            }}
                          >
                            <ListItemIcon>
                              <BarIcon sx={{ color: '#6B8F71' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box component="span" sx={{ display: 'block' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: alpha('#fff', 0.9) }}>
                                    {transaction.quantity}x {transaction.drink_name}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                                    {new Date(transaction.timestamp).toLocaleDateString()} at {new Date(transaction.timestamp).toLocaleTimeString()}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#6B8F71',
                                }}
                              >
                                {(transaction.price * transaction.quantity).toFixed(2)} CHF
                              </Typography>
                              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
                                {transaction.price.toFixed(2)} CHF each
                              </Typography>
                            </Box>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                    {barTransactions.length > 3 && (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                          Showing 3 of {barTransactions.length} transactions
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Achievements Section */}
        <Grid item xs={12}>
          <Card 
            sx={{ 
              backgroundColor: '#241E18',
              border: `1px solid ${alpha('#C9A227', 0.2)}`,
              borderRadius: 3,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <AchievementIcon 
                  sx={{ 
                    fontSize: 32, 
                    color: '#C9A227',
                  }} 
                />
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 800,
                    color: '#C9A227',
                  }}
                >
                  Achievements
                </Typography>
                <Chip
                  label={`${profileStats.achievementPoints} Points`}
                  sx={{
                    bgcolor: alpha('#C9A227', 0.2),
                    color: '#C9A227',
                    fontWeight: 700,
                    border: `1px solid ${alpha('#C9A227', 0.3)}`,
                  }}
                />
              </Box>
              <AchievementGrid achievements={achievements} loading={achievementsLoading} />
            </CardContent>
          </Card>
        </Grid>


        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card 
            sx={{ 
              backgroundColor: '#241E18',
              border: `1px solid ${alpha('#3D7A8C', 0.2)}`,
              borderRadius: 3,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: '#3D7A8C',
                }}
              >
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isOwnProfile && (
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate('/settings')}
                    sx={{
                      borderColor: alpha('#3D7A8C', 0.5),
                      color: '#3D7A8C',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        borderColor: '#3D7A8C',
                        bgcolor: alpha('#3D7A8C', 0.1),
                      }
                    }}
                  >
                    Profile Settings
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<TeamIcon />}
                  onClick={() => navigate('/team-create')}
                  sx={{
                    borderColor: alpha('#C9A227', 0.5),
                    color: '#C9A227',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: '#C9A227',
                      bgcolor: alpha('#C9A227', 0.1),
                    }
                  }}
                >
                  Create Team
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GameIcon />}
                  onClick={() => navigate('/tournaments')}
                  sx={{
                    borderColor: alpha('#C47B5A', 0.5),
                    color: '#C47B5A',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: '#C47B5A',
                      bgcolor: alpha('#C47B5A', 0.1),
                    }
                  }}
                >
                  Join Tournament
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Game Account Dialog */}
      <Dialog 
        open={gameAccountDialog} 
        onClose={closeGameAccountDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GameIcon sx={{ mr: 1 }} />
            Add Game Account
          </Box>
          <IconButton onClick={closeGameAccountDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Game Name"
                name="gameName"
                value={newGameAccount.gameName}
                onChange={handleGameAccountChange}
                variant="outlined"
                placeholder="e.g., League of Legends, Valorant, CS:GO"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account ID / Username"
                name="accountId"
                value={newGameAccount.accountId}
                onChange={handleGameAccountChange}
                variant="outlined"
                placeholder="Your in-game username or ID"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeGameAccountDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGameAccount}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
            disabled={loading || !newGameAccount.gameName.trim() || !newGameAccount.accountId.trim()}
          >
            {loading ? 'Adding...' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Profile;